import { useEffect } from 'react';

/**
 * Closes an open disclosure (the header's account menu or mobile menu panel):
 * - Escape: closes and returns focus to the trigger;
 * - a pointer press outside `containerRef`: closes (focus stays where the press put it);
 * - focus moving to an element outside `containerRef` (Tab past the end): closes.
 * Listeners exist only while `open` and are removed on close and unmount.
 */
export default function useDismiss(open, close, containerRef, triggerRef) {
  useEffect(() => {
    if (!open) return undefined;
    const inside = (node) => Boolean(node && containerRef.current && containerRef.current.contains(node));

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      close();
      if (triggerRef && triggerRef.current) triggerRef.current.focus();
    };
    const onPointerDown = (e) => {
      if (!inside(e.target)) close();
    };
    const onFocusIn = (e) => {
      if (!inside(e.target)) close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open, close, containerRef, triggerRef]);
}
